<script setup lang="ts">
import { TrashIcon } from '@heroicons/vue/24/solid';
import Checkbox from 'primevue/checkbox';
import type { IMobileNumber } from '~/interfaces/dto/auth/response';

const props = withDefaults(defineProps<{ number: IMobileNumber | null }>(), {
  number: null,
});

const site = useSiteStore();
const { data, allNumbers, requestOtp } = useMobileNumberHandler();

const emits = defineEmits<{
  (e: 'delete', number: string): void;
  (e: 'update', number: string): void;
  (e: 'change-primary', number: string): void;
  (e: 'update:modelValue', newPrimaryList: string[][]): void;
}>();

const isPrimary = computed(() => props.number?.primaryNumber);

const setPrimaryNumberHandler = () => {
  if (isPrimary.value) return;
  if (props.number.mobileNumber === data.primaryNumber?.mobileNumber) return;
  data.desiredPrimaryNumber = props.number.mobileNumber.trim();
  site.activateModal('numberChangeLogoutAlert');
};

const openVerificationModal = () => {
  data.numberToVerify = props.number;
  site.activateModal('verifyNumberOtp');
};

const dialingCodeLength = computed(() => site.getDialingCode.length - 1);
</script>

<template>
  <div class="flex w-full items-center justify-between gap-4">
    <div class="flex items-center gap-2 sm:w-full">
      <div
        class="flex dark:bg-dark-900 bg-light-900 py-1 pl-2 sm:px-2 rounded-lg gap-2 w-[150px] cursor-default"
        :class="{
          '!px-2': isPrimary,
          'w-fit': allNumbers.length < 2,
        }"
      >
        <div class="flex flex-col items-start justify-center gap-0.5">
          <span class="text-xs text-dark-100 font-light truncate text-ellipsis">
            {{ $t('mobile-number') }}
          </span>
          <span class="font-bold text-xs text-dark-100 truncate text-ellipsis">
            {{
              `${site.getDialingCode} ${number?.mobileNumber?.slice(dialingCodeLength, 7)} ${number?.mobileNumber?.slice(7, 11)}`
            }}
          </span>
        </div>
        <DesignSystemButton
          v-if="!isPrimary"
          type="tertiary"
          class="!bg-transparent !text-light-50"
          @click="emits('delete', number.mobileNumber)"
        >
          <TrashIcon class="!w-5 !h-5" />
        </DesignSystemButton>
      </div>
    </div>
    <div
      class="ml-auto flex items-center gap-2 max-w-1/2 sm:min-w-fit overflow-hidden"
      :class="{
        'pointer-events-none': isPrimary,
      }"
      @click="
        number.verified ? setPrimaryNumberHandler() : openVerificationModal()
      "
    >
      <label
        v-if="number.verified || isPrimary"
        :for="number.mobileNumber"
        class="truncate text-ellipsis capitalize"
        :class="{ 'font-bold': isPrimary }"
      >
        {{ isPrimary ? $t('primary') : $t('set-as-primary') }}
      </label>
      <Checkbox
        v-if="number.verified || isPrimary"
        v-model="isPrimary"
        :input-id="number.mobileNumber"
        binary
        :pt="{
          box: () => ({
            class: `w-6 border rounded-md dark:border-dark-600 border-light-600 flex items-center justify-center pointer-events-none ${
              isPrimary
                ? 'bg-primary-blue-gradient border-none cursor-not-allowed'
                : `dark:bg-dark-800 bg-light-400 [&>[data-pc-section='icon']]:hidden`
            }`,
          }),
        }"
      />
      <DesignSystemButton v-else padding="sm" class="text-xs" rounding="md">
        {{ $t('verify') }}
      </DesignSystemButton>
    </div>
  </div>
</template>
